import type { AreaSymbol } from "@/lib/layouts/types";

/**
 * Drag-and-drop payload format dla okien (admin only).
 *
 * Browsers nie eksponują dataTransfer.getData() w `dragover`/`dragenter`
 * (security: pełen content widoczny tylko w `drop`). Stąd używamy custom
 * MIME type — tylko types[] jest dostępny w dragover, co pozwala odróżnić
 * "to nasze drag" od "to drag z systemu" (np. plik), ale szczegóły dopiero
 * w drop.
 *
 * **Konsumenci:**
 *   - `LayoutAndWindowsPanel.tsx` — drag start z panelu unpublished windows
 *
 * Po unifikacji visit page (2026-05-12 v2) widok strony NIE ma już osobnego
 * trybu edycji z drop-target'ami — drag happens później (gdy dodawanie okien
 * przez panel zostanie odbudowane na nowych zasadach, np. drop overlay na
 * visitor view).
 */

export const DRAG_MIME = "application/x-imperial-window";

export const DRAG_FROM_PANEL = "fromPanel" as const;
export const DRAG_FROM_AREA = "fromArea" as const;

export type DragPayloadFromPanel = {
  type: typeof DRAG_FROM_PANEL;
  windowId: number;
};

export type DragPayloadFromArea = {
  type: typeof DRAG_FROM_AREA;
  windowId: number;
  fromArea: AreaSymbol;
  fromIndex: number;
};

export type DragPayload = DragPayloadFromPanel | DragPayloadFromArea;

export function parseDragPayload(raw: string): DragPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.type === DRAG_FROM_PANEL && typeof parsed.windowId === "number") {
      return parsed as DragPayloadFromPanel;
    }
    if (
      parsed?.type === DRAG_FROM_AREA &&
      typeof parsed.windowId === "number" &&
      typeof parsed.fromArea === "string" &&
      typeof parsed.fromIndex === "number"
    ) {
      return parsed as DragPayloadFromArea;
    }
  } catch {
    /* invalid JSON -> null */
  }
  return null;
}
